import {
  Box,
  Button,
  Flex,
  Grid,
  IconButton,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Text,
  useDisclosure,
  useOutsideClick,
} from "@chakra-ui/react";
import { IMaskInput } from "react-imask";
import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addDays,
  addMonths,
  addYears,
  endOfDay,
  endOfMonth,
  format,
  getDay,
  getHours,
  getMinutes,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isValid,
  parse,
  set,
  setMonth,
  setYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subYears,
} from "date-fns";
import { range } from "lodash";
import { DATE_FORMAT, DATETIME_FORMAT } from "../../constants";
import CustomIcon from "../../../../public/icons-v1_1";
import AppSelect from "@/features/line-sheet-sets/app-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useDateFnsLocale from "@/shared/hooks/useDateFnsLocale";

function formatValue(value: Date | undefined, withTime: boolean): string {
  if (!value) return "";
  return format(value, withTime ? DATETIME_FORMAT : DATE_FORMAT);
}

/** 일 단위로 min/max 범위 안에 있는지 확인한다. */
function isDayDisabled(day: Date, minDate?: Date, maxDate?: Date): boolean {
  const beforeMin = !!minDate && isBefore(endOfDay(day), startOfDay(minDate));
  const afterMax = !!maxDate && isAfter(startOfDay(day), endOfDay(maxDate));
  return beforeMin || afterMax;
}

type CommitResult =
  | { status: "empty" }
  | { status: "incomplete" }
  | { status: "invalid" }
  | { status: "outOfRange" }
  | { status: "ok"; date: Date };

/** 마스킹된 입력값을 검증해 확정 결과(빈칸/미완성/불가능/범위밖/정상)를 반환한다. */
function evaluateCommit(params: {
  maskedValue: string; // 예 "2020-02-01 10:30" 또는 placeholder 포함 "2020-0_-__ __:__"
  showTimeInput: boolean;
  minDate?: Date;
  maxDate?: Date;
}): CommitResult {
  const { maskedValue, showTimeInput, minDate, maxDate } = params;

  const digits = maskedValue.replace(/\D/g, ""); // 숫자만(구분자/placeholder 제외)
  const maxDigits = showTimeInput ? 12 : 8;

  if (digits.length === 0) return { status: "empty" };
  if (digits.length < maxDigits) return { status: "incomplete" };

  // 완성 시 maskedValue는 정규 포맷 문자열이므로 그대로 파싱한다.
  const fmt = showTimeInput ? DATETIME_FORMAT : DATE_FORMAT;
  const parsed = parse(maskedValue, fmt, new Date());
  if (!isValid(parsed)) return { status: "invalid" }; // 2026-13-40 등

  if (isDayDisabled(parsed, minDate, maxDate)) return { status: "outOfRange" };

  const date = showTimeInput
    ? set(parsed, { seconds: 0, milliseconds: 0 })
    : startOfDay(parsed);
  return { status: "ok", date };
}

/** 달력 셀(날짜)의 글자색을 결정한다. */
function getDayTextColor(
  day: Date,
  isSelected: boolean,
  isCurrentMonth: boolean
): string {
  if (isSelected) return "#FFF";
  if (!isCurrentMonth) return "#C4C4C2";
  const weekday = getDay(day);
  if (weekday === 0) return "#E53E3E"; // 일요일
  if (weekday === 6) return "#1272EF"; // 토요일
  return "#444440";
}

// IMask 패턴 마스크(0=숫자 전용 정의) → 숫자만 입력 가능.
const DATE_IMASK = "0000-00-00";
const DATETIME_IMASK = "0000-00-00 00:00";

// 연도 그리드 페이지(12년)의 시작 연도. 예: 2026 → 2016.
const YEAR_PAGE_SIZE = 12;
const yearPageStartOf = (year: number) =>
  year - (((year % YEAR_PAGE_SIZE) + YEAR_PAGE_SIZE) % YEAR_PAGE_SIZE);

export interface CustomDatePickerProps {
  value: Date | undefined;
  onChange: (value: Date | undefined) => void;
  /** 시간(시/분)까지 입력/선택할지 여부 */
  showTimeInput?: boolean;
  width?: string;
  placeholder?: string;
  name?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isClearable?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /** 입력값의 유효성(형식)이 바뀔 때 호출. 잘못된 형식이면 isValid=false. */
  onValidityChange?: (isValid: boolean) => void;
}

export default function CustomDatePicker({
  value,
  onChange,
  showTimeInput = true,
  width = "100%",
  name,
  isDisabled = false,
  isReadOnly = false,
  isClearable = true,
  minDate,
  maxDate,
  onValidityChange,
}: CustomDatePickerProps) {
  const { t, tTitle, isKor } = useI18nHelper();
  const locale = useDateFnsLocale();

  const { isOpen, onClose, onToggle } = useDisclosure();

  // ── 마스크 입력: react-imask로 처리 ──────────────────────────
  // maskedValue: 화면/입력의 단일 소스(마스킹된 문자열, 예 "2020-02-01 10:30" 또는 "____-__-__ __:__").
  const [maskedValue, setMaskedValue] = useState<string>(() =>
    formatValue(value, showTimeInput)
  );
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const imaskPattern = showTimeInput ? DATETIME_IMASK : DATE_IMASK;

  const [viewMonth, setViewMonth] = useState<Date>(
    startOfMonth(value ?? new Date())
  );

  // 캘린더 뷰: 일 / 월 / 연 그리드 전환.
  const [calendarView, setCalendarView] = useState<"day" | "month" | "year">(
    "day"
  );
  // 연도 그리드에 보여줄 12년 페이지의 시작 연도.
  const [yearPageStart, setYearPageStart] = useState<number>(() =>
    yearPageStartOf(getYear(value ?? new Date()))
  );

  // 월 이동 시 날짜 칸 transition으로 인한 깜빡임을 막기 위해
  // "그 한 번의 갱신 프레임"에만 transition을 끈다(hover 등 평소 전환은 유지).
  const [suppressDayTransition, setSuppressDayTransition] = useState(false);
  const rafIdsRef = useRef<number[]>([]);
  const suppressDayTransitionOnce = useCallback(() => {
    setSuppressDayTransition(true);
    // double rAF: suppress 상태가 한 프레임 그려진 뒤 다음 프레임에 복원.
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => setSuppressDayTransition(false));
      rafIdsRef.current.push(raf2);
    });
    rafIdsRef.current.push(raf1);
  }, []);
  useEffect(() => () => rafIdsRef.current.forEach(cancelAnimationFrame), []);

  const containerRef = useRef<HTMLDivElement>(null);

  // 형식 오류 여부. true면 빨간 테두리 + 에러 메시지를 표시한다.
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  // onValidityChange의 최신 참조를 ref로 들고가 deps 루프를 피한다.
  const onValidityChangeRef = useRef(onValidityChange);
  useEffect(() => {
    onValidityChangeRef.current = onValidityChange;
  });

  // 형식 오류 여부를 내부 상태에 반영하고 부모에도 알린다.
  // setInvalid(true) = 형식 오류. 외부 콜백 계약은 isValid이므로 반전해서 전달한다.
  const setInvalid = useCallback((invalid: boolean) => {
    setIsInvalid(invalid);
    onValidityChangeRef.current?.(!invalid);
  }, []);

  // 언마운트 시 에러 신호를 해제(부모가 막아둔 제출 버튼을 풀어준다).
  useEffect(() => () => onValidityChangeRef.current?.(true), []);

  // 외부 value 변경 시 입력 동기화 + 형식 오류 해제
  useEffect(() => {
    setMaskedValue(formatValue(value, showTimeInput));
    setInvalid(false);
  }, [value, showTimeInput, setInvalid]);

  // 캘린더를 열 때 현재 값의 월로 이동하고 일(day) 뷰로 초기화
  useEffect(() => {
    if (isOpen) {
      setViewMonth(startOfMonth(value ?? new Date()));
      setCalendarView("day");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const hour = value ? getHours(value) : 0;
  const minute = value ? getMinutes(value) : 0;

  const hourOptions = useMemo(
    () =>
      range(0, 24).map(
        (i): AppSelectOption<number> => ({
          name: String(i).padStart(2, "0"),
          value: i,
        })
      ),
    []
  );

  const minuteOptions = useMemo(
    () =>
      range(0, 60).map(
        (i): AppSelectOption<number> => ({
          name: String(i).padStart(2, "0"),
          value: i,
        })
      ),
    []
  );

  // 요일 헤더 (일요일 시작)
  const weekdayLabels = useMemo(() => {
    const sunday = startOfWeek(new Date(), { weekStartsOn: 0 });
    return range(0, 7).map((i) =>
      format(addDays(sunday, i), "EEE", { locale })
    );
  }, [locale]);

  // 달력 그리드 (6주 x 7일, 일요일 시작)
  const monthDays = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(viewMonth), {
      weekStartsOn: 0,
    });
    return range(0, 42).map((i) => addDays(gridStart, i));
  }, [viewMonth]);

  // 월 그리드용 짧은 월 이름(로케일).
  const monthLabels = useMemo(
    () =>
      range(0, 12).map((i) =>
        format(setMonth(new Date(), i), "MMM", { locale })
      ),
    [locale]
  );
  // 연도 그리드용 12개 연도.
  const yearList = useMemo(
    () => range(yearPageStart, yearPageStart + YEAR_PAGE_SIZE),
    [yearPageStart]
  );

  // 월/연 범위 비활성 판정.
  const isYearDisabled = useCallback(
    (year: number) =>
      (!!minDate && year < getYear(minDate)) ||
      (!!maxDate && year > getYear(maxDate)),
    [minDate, maxDate]
  );
  const isMonthDisabled = useCallback(
    (monthDate: Date) =>
      (!!minDate && endOfMonth(monthDate) < startOfDay(minDate)) ||
      (!!maxDate && startOfMonth(monthDate) > endOfDay(maxDate)),
    [minDate, maxDate]
  );

  const commitInput = useCallback(() => {
    const result = evaluateCommit({
      maskedValue,
      showTimeInput,
      minDate,
      maxDate,
    });

    switch (result.status) {
      case "empty": // 빈칸 = 값 해제
        setInvalid(false);
        if (value) onChange(undefined);
        return;
      case "incomplete": // 미완성
      case "invalid": // 불가능한 날짜 → 에러 표시(입력값 유지)
        setInvalid(true);
        return;
      case "outOfRange": // 허용 범위 밖 → 이전 값으로 되돌림
        setMaskedValue(formatValue(value, showTimeInput));
        setInvalid(false);
        return;
      case "ok":
        setInvalid(false);
        onChange(result.date);
        setViewMonth(startOfMonth(result.date));
        return;
    }
  }, [
    maskedValue,
    showTimeInput,
    value,
    onChange,
    minDate,
    maxDate,
    setInvalid,
  ]);

  // Enter: 입력을 확정/검증한 뒤 팝오버를 닫는다.
  const handleEnterKey = useCallback(() => {
    commitInput();
    onClose();
  }, [commitInput, onClose]);

  // Escape: 입력을 이전 값으로 되돌리고 에러를 해제한 뒤 닫는다(취소).
  const handleEscapeKey = useCallback(() => {
    setMaskedValue(formatValue(value, showTimeInput));
    setInvalid(false);
    onClose();
  }, [value, showTimeInput, setInvalid, onClose]);

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleEnterKey();
      } else if (event.key === "Escape") {
        handleEscapeKey();
      }
    },
    [handleEnterKey, handleEscapeKey]
  );

  // 날짜 선택(값 확정). 팝오버 닫기는 호출부에서 명시적으로 처리한다.
  const handleDaySelect = useCallback(
    (day: Date) => {
      if (isDayDisabled(day, minDate, maxDate)) return;

      // 다른 달의 날짜를 클릭하면 그 달로 이동하면서 선택도 한다
      if (!isSameMonth(day, viewMonth)) {
        suppressDayTransitionOnce();
        setViewMonth(startOfMonth(day));
      }

      const base = value ?? new Date();
      const next = set(day, {
        hours: showTimeInput ? getHours(base) : 0,
        minutes: showTimeInput ? getMinutes(base) : 0,
        seconds: 0,
        milliseconds: 0,
      });
      setInvalid(false);
      onChange(next);
    },
    [
      value,
      showTimeInput,
      onChange,
      minDate,
      maxDate,
      viewMonth,
      setInvalid,
      suppressDayTransitionOnce,
    ]
  );

  const handleTimeChange = useCallback(
    (part: "hours" | "minutes", raw: number) => {
      const base = value ?? new Date();
      const next = set(base, { [part]: raw, seconds: 0, milliseconds: 0 });
      setInvalid(false);
      onChange(next);
    },
    [value, onChange, setInvalid]
  );

  const handleClear = useCallback(() => {
    onChange(undefined);
    setMaskedValue("");
    setInvalid(false);
  }, [onChange, setInvalid]);

  // 연/월 선택을 즉시 값에 반영한다(day 클릭처럼). 기존 일/시/분은 유지하고
  // 대상 연·월만 바꿔 onChange로 확정 후 day 뷰로 복귀한다.
  const applyYearMonth = useCallback(
    (nextViewMonth: Date) => {
      const base = value ?? startOfMonth(nextViewMonth); // 값 없으면 1일·0시
      const next = set(base, {
        year: getYear(nextViewMonth),
        month: getMonth(nextViewMonth),
        seconds: 0,
        milliseconds: 0,
      });
      const normalized = showTimeInput ? next : startOfDay(next);
      setInvalid(false);
      onChange(normalized);
      setViewMonth(startOfMonth(nextViewMonth));
      setCalendarView("day");
    },
    [value, showTimeInput, onChange, setInvalid]
  );

  // 월 그리드에서 월 선택 → 그 달로 즉시 반영(day 뷰 복귀).
  const handleMonthSelect = useCallback(
    (monthIndex: number) => applyYearMonth(setMonth(viewMonth, monthIndex)),
    [applyYearMonth, viewMonth]
  );

  // 연도 그리드에서 연 선택 → 그 해로 즉시 반영(day 뷰 복귀, 연도 선택은 여기서 끝).
  // 월 변경은 헤더의 월 숫자 클릭(goToMonthView)으로만 진입한다.
  const handleYearSelect = useCallback(
    (year: number) => applyYearMonth(setYear(viewMonth, year)),
    [applyYearMonth, viewMonth]
  );

  // 헤더 ◀: 뷰별로 이전 월/연/연도페이지로 이동.
  const handlePrevClick = useCallback(() => {
    if (calendarView === "day") {
      suppressDayTransitionOnce();
      setViewMonth((m) => subMonths(m, 1));
    } else if (calendarView === "month") setViewMonth((m) => subYears(m, 1));
    else setYearPageStart((s) => s - YEAR_PAGE_SIZE);
  }, [calendarView, suppressDayTransitionOnce]);

  // 헤더 ▶: 뷰별로 다음 월/연/연도페이지로 이동.
  const handleNextClick = useCallback(() => {
    if (calendarView === "day") {
      suppressDayTransitionOnce();
      setViewMonth((m) => addMonths(m, 1));
    } else if (calendarView === "month") setViewMonth((m) => addYears(m, 1));
    else setYearPageStart((s) => s + YEAR_PAGE_SIZE);
  }, [calendarView, suppressDayTransitionOnce]);

  // 헤더 연도 숫자 클릭 → 연도 선택(year) 뷰.
  const goToYearView = useCallback(() => {
    setYearPageStart(yearPageStartOf(getYear(viewMonth)));
    setCalendarView("year");
  }, [viewMonth]);

  // 헤더 월 숫자 클릭 → 월 선택(month) 뷰.
  const goToMonthView = useCallback(() => setCalendarView("month"), []);

  // 컨테이너(인풋 + 캘린더 + 중첩된 시/분 셀렉터)를 기준으로 바깥 클릭만 닫는다.
  // closeOnBlur 대신 직접 처리: 시/분 셀렉터를 닫을 때 포커스가 body로 떨어져도
  // 캘린더가 같이 닫히지 않도록 한다.
  useOutsideClick({
    enabled: isOpen,
    ref: containerRef,
    handler: () => {
      commitInput();
      onClose();
    },
  });

  const interactive = !isDisabled && !isReadOnly;

  // 박스(테두리)의 상태색: 오류 > 포커스 > 기본.
  const boxBorderColor = isInvalid
    ? "#E5594F"
    : isFocused
    ? "#1272EF"
    : "#8F8F8C";

  return (
    <Box width={width} ref={containerRef}>
      <Popover
        isOpen={isOpen}
        onClose={() => {
          commitInput();
          onClose();
        }}
        placement="bottom-start"
        strategy="fixed"
        isLazy
        autoFocus={false}
        closeOnBlur={false}
      >
        <PopoverAnchor>
          <Box
            position="relative"
            width="100%"
            minH="28px"
            display="flex"
            alignItems="center"
            borderWidth="0.5px"
            borderStyle="solid"
            borderColor={boxBorderColor}
            borderRadius="4px"
            boxShadow={
              isFocused || isInvalid ? `0 0 0 1px ${boxBorderColor}` : undefined
            }
            backgroundColor={isDisabled ? "#EFEFED" : "#FFF"}
            pl="12px"
            pr={isClearable ? "56px" : "36px"}
            _hover={{
              borderColor: isInvalid
                ? "#E5594F"
                : interactive
                ? "#444440"
                : undefined,
            }}
          >
            {/* react-imask 입력: placeholder(____)는 항상 보이고 입력 시 숫자로 치환.
                중간 클릭/캐럿/붙여넣기는 라이브러리가 처리. 테두리·상태색은 바깥 Box가 담당. */}
            <IMaskInput
              mask={imaskPattern}
              lazy={false}
              placeholderChar="_"
              overwrite={false}
              inputRef={inputRef}
              value={maskedValue}
              onAccept={(val: string) => setMaskedValue(val)}
              name={name}
              inputMode="numeric"
              disabled={isDisabled}
              readOnly={isReadOnly}
              autoComplete="off"
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                commitInput();
              }}
              onKeyDown={handleInputKeyDown}
              onClick={() => {
                // 캘린더가 열린 상태에서 입력하려고 누르면 캘린더를 닫는다.
                if (isOpen) onClose();
              }}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                padding: 0,
                font: "inherit",
                fontSize: "12px",
                fontWeight: 600,
                color: isDisabled ? "#6F6F6C" : "#444444",
                // 포커스 아님 + 값 있음 + 형식 올바름일 때만 밑줄.
                textDecoration:
                  !isFocused && !!value && !isInvalid ? "underline" : "none",
                // 밑줄과 텍스트 사이 거리(값을 키우면 더 멀어짐).
                textUnderlineOffset: "2px",
              }}
            />

            {/* 우측 아이콘 (clear / 캘린더) */}
            <Flex
              position="absolute"
              right="2px"
              top="0"
              height="100%"
              align="center"
              gap="2px"
              zIndex={1}
            >
              {isClearable && value && interactive && (
                <IconButton
                  aria-label="clear"
                  icon={<CustomIcon src={"x"} width={16} height={16} />}
                  size="xs"
                  variant="ghost"
                  color="#8F8F8C"
                  minW="20px"
                  h="20px"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                />
              )}
              <IconButton
                aria-label="open calendar"
                icon={<CustomIcon src={"calendar"} width={24} height={24} />}
                size="xs"
                variant="ghost"
                color="#444444"
                minW="20px"
                h="20px"
                isDisabled={isDisabled || isReadOnly}
                onClick={(e) => {
                  e.stopPropagation();
                  if (interactive) onToggle();
                }}
              />
            </Flex>
          </Box>
        </PopoverAnchor>

        <PopoverContent width="280px" _focusVisible={{ outline: "none" }}>
          <PopoverBody p="12px">
            {/* 이동 헤더 (뷰에 따라 월/연/연도페이지 이동) */}
            <Flex align="center" justify="space-between" mb="8px">
              <IconButton
                aria-label="prev"
                icon={
                  <CustomIcon src={"chevron-left"} width={24} height={24} />
                }
                size="xs"
                variant="ghost"
                onClick={handlePrevClick}
              />
              <Flex
                align="baseline"
                gap="2px"
                fontSize="13px"
                fontWeight="bold"
                color="#444440"
              >
                {calendarView === "year" ? (
                  <Text>{`${yearList[0]} - ${
                    yearList[yearList.length - 1]
                  }`}</Text>
                ) : (
                  <>
                    <Text
                      as="span"
                      cursor="pointer"
                      borderBottom="1.5px solid #444440"
                      _hover={{ color: "#1272EF", borderColor: "#1272EF" }}
                      onClick={goToYearView}
                    >
                      {getYear(viewMonth)}
                    </Text>
                    <Text
                      as="span"
                      style={{
                        marginRight: "4px",
                      }}
                    >
                      {isKor ? "년" : "."}
                    </Text>
                    {calendarView === "day" && (
                      <>
                        <Text
                          as="span"
                          cursor="pointer"
                          borderBottom="1.5px solid #444440"
                          _hover={{ color: "#1272EF", borderColor: "#1272EF" }}
                          onClick={goToMonthView}
                        >
                          {format(viewMonth, "MM")}
                        </Text>
                        <Text as="span">{isKor ? "월" : ""}</Text>
                      </>
                    )}
                  </>
                )}
              </Flex>
              <IconButton
                aria-label="next"
                icon={
                  <CustomIcon src={"chevron-right"} width={24} height={24} />
                }
                size="xs"
                variant="ghost"
                onClick={handleNextClick}
              />
            </Flex>

            {/* day 뷰: 요일 헤더 + 날짜 그리드 */}
            {calendarView === "day" && (
              <>
                <Grid templateColumns="repeat(7, 1fr)" mb="2px">
                  {weekdayLabels.map((label, index) => (
                    <Text
                      key={label}
                      fontSize="11px"
                      textAlign="center"
                      py="4px"
                      color={
                        index === 0
                          ? "#E53E3E"
                          : index === 6
                          ? "#1272EF"
                          : "#8F8F8C"
                      }
                    >
                      {label}
                    </Text>
                  ))}
                </Grid>

                {/* 날짜 그리드 */}
                <Grid templateColumns="repeat(7, 1fr)" gap="2px">
                  {monthDays.map((day, index) => {
                    const isCurrentMonth = isSameMonth(day, viewMonth);
                    const isSelected = !!value && isSameDay(day, value);
                    const today = isToday(day);
                    const disabled = isDayDisabled(day, minDate, maxDate);

                    return (
                      <Button
                        // 셀 "위치" 기준 key. 같은 DOM 노드를 재사용해 내용만 갱신하므로
                        // 월 이동이 매끄럽다. 이때 발생하는 bg transition 깜빡임은
                        // suppressDayTransition으로 그 프레임만 꺼서 막는다.
                        key={index}
                        size="xs"
                        variant="ghost"
                        h="30px"
                        minW="0"
                        borderRadius="4px"
                        transition={suppressDayTransition ? "none" : undefined}
                        fontSize="12px"
                        fontWeight={isSelected ? "bold" : "normal"}
                        isDisabled={disabled}
                        color={getDayTextColor(day, isSelected, isCurrentMonth)}
                        bg={isSelected ? "#1272EF" : "transparent"}
                        border={
                          today && !isSelected ? "1px solid #1272EF" : undefined
                        }
                        _hover={{
                          bg: isSelected ? "#1272EF" : "#E8F1FE",
                        }}
                        onClick={() => {
                          handleDaySelect(day);
                          // 시간 입력이 없으면 선택 즉시 닫는다.
                          if (!showTimeInput) onClose();
                        }}
                      >
                        {format(day, "d")}
                      </Button>
                    );
                  })}
                </Grid>
              </>
            )}

            {/* month 뷰: 12개월 그리드 */}
            {calendarView === "month" && (
              <Grid templateColumns="repeat(3, 1fr)" gap="6px">
                {monthLabels.map((label, index) => {
                  const isSelected = index === getMonth(viewMonth);
                  const disabled = isMonthDisabled(setMonth(viewMonth, index));
                  return (
                    <Button
                      key={index}
                      size="xs"
                      variant="ghost"
                      h="44px"
                      minW="0"
                      borderRadius="4px"
                      fontSize="12px"
                      fontWeight={isSelected ? "bold" : "normal"}
                      isDisabled={disabled}
                      color={isSelected ? "#FFF" : "#444440"}
                      bg={isSelected ? "#1272EF" : "transparent"}
                      _hover={{ bg: isSelected ? "#1272EF" : "#E8F1FE" }}
                      onClick={() => handleMonthSelect(index)}
                    >
                      {label}
                    </Button>
                  );
                })}
              </Grid>
            )}

            {/* year 뷰: 12년 그리드 */}
            {calendarView === "year" && (
              <Grid templateColumns="repeat(3, 1fr)" gap="6px">
                {yearList.map((year, index) => {
                  const isSelected = year === getYear(viewMonth);
                  const disabled = isYearDisabled(year);
                  return (
                    <Button
                      key={index}
                      size="xs"
                      variant="ghost"
                      h="44px"
                      minW="0"
                      borderRadius="4px"
                      fontSize="12px"
                      fontWeight={isSelected ? "bold" : "normal"}
                      isDisabled={disabled}
                      color={isSelected ? "#FFF" : "#444440"}
                      bg={isSelected ? "#1272EF" : "transparent"}
                      _hover={{ bg: isSelected ? "#1272EF" : "#E8F1FE" }}
                      onClick={() => handleYearSelect(year)}
                    >
                      {year}
                    </Button>
                  );
                })}
              </Grid>
            )}

            {/* 시간 선택 (day 뷰에서만) */}
            {showTimeInput && calendarView === "day" && (
              <Flex
                align="center"
                justify="center"
                gap="6px"
                mt="10px"
                pt="10px"
                borderTop="1px solid #EFEFED"
              >
                <AppSelect<number>
                  name={tTitle("hour")}
                  width="88px"
                  options={hourOptions}
                  value={hour}
                  isClearable={false}
                  isSearchable={false}
                  popoverStrategy="fixed"
                  onSelect={(h) => {
                    if (h !== undefined) handleTimeChange("hours", h);
                  }}
                />
                <Text fontSize="13px" color="#8F8F8C">
                  :
                </Text>
                <AppSelect<number>
                  name={tTitle("minute")}
                  width="88px"
                  options={minuteOptions}
                  value={minute}
                  isClearable={false}
                  isSearchable={false}
                  popoverStrategy="fixed"
                  onSelect={(m) => {
                    if (m !== undefined) handleTimeChange("minutes", m);
                  }}
                />
              </Flex>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
      {isInvalid && (
        <Text mt="4px" fontSize="12px" color="#E5594F">
          {t("common:messages.invalid_date_format")}
        </Text>
      )}
    </Box>
  );
}
